import { useState } from 'react'
import { Loader2, Pencil, Plus, Settings2, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import {
  TEXTE_MAX,
  TITRE_MAX,
  useCreerReponse,
  useModifierReponse,
  useReponsesRapides,
  useSupprimerReponse,
  type ReponseRapide,
} from '@/api/reponsesRapides'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

const sansAccents = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()

/** Les réponses dont le titre ou le texte contient la recherche (sans accents). */
export function filtrerReponses(reponses: ReponseRapide[], requete: string) {
  const q = sansAccents(requete.trim())
  if (!q) return reponses
  return reponses.filter((r) => sansAccents(r.title).includes(q) || sansAccents(r.text).includes(q))
}

interface ListeProps {
  reponses: ReponseRapide[]
  /** Toutes les réponses de l'entreprise (avant la recherche) : liste vide ≠ aucun résultat. */
  total: number
  chargement: boolean
  index: number
  onIndex: (i: number) => void
  onChoisir: (r: ReponseRapide) => void
  peutGerer: boolean
  onGerer: () => void
  darkMode: boolean
}

/** La petite liste au-dessus du champ (icône ou « / »). Les clics passent par
 * mousedown pour garder le focus dans le champ. */
export function ListeReponses({ reponses, total, chargement, index, onIndex, onChoisir, peutGerer, onGerer, darkMode }: ListeProps) {
  const { t } = useTranslation()
  const pied = cn(
    'w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] font-medium border-t',
    darkMode
      ? 'border-slate-800 text-indigo-300 hover:bg-slate-800'
      : 'border-slate-100 text-indigo-600 hover:bg-slate-50',
  )
  return (
    <div
      role="listbox"
      className={cn(
        'absolute bottom-full left-4 mb-1 z-50 w-80 max-w-[calc(100%-2rem)] rounded-lg border shadow-lg overflow-hidden',
        darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200',
      )}
    >
      {chargement ? (
        <div className={cn('flex justify-center py-4', darkMode ? 'text-slate-500' : 'text-slate-400')}>
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : reponses.length === 0 ? (
        <p className={cn('px-3 py-3 text-[13px]', darkMode ? 'text-slate-400' : 'text-slate-500')}>
          {total === 0
            ? t('reponsesRapides.aucune', 'Aucune réponse enregistrée')
            : t('reponsesRapides.aucunResultat', 'Aucune réponse trouvée')}
        </p>
      ) : (
        <div className="max-h-64 overflow-y-auto py-1">
          {reponses.map((r, i) => (
            <button
              key={r.id}
              type="button"
              role="option"
              aria-selected={i === index}
              onMouseDown={(e) => {
                e.preventDefault()
                onChoisir(r)
              }}
              onMouseEnter={() => onIndex(i)}
              className={cn(
                'w-full px-3 py-1.5 text-left',
                i === index ? (darkMode ? 'bg-slate-800' : 'bg-slate-100') : '',
              )}
            >
              <span className={cn('block truncate text-[13px] font-medium', darkMode ? 'text-white' : 'text-slate-900')}>
                {r.title}
              </span>
              <span className={cn('block truncate text-xs', darkMode ? 'text-slate-400' : 'text-slate-500')}>{r.text}</span>
            </button>
          ))}
        </div>
      )}
      {peutGerer && !chargement && (
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault()
            onGerer()
          }}
          className={pied}
        >
          {total === 0 ? <Plus className="h-3.5 w-3.5" /> : <Settings2 className="h-3.5 w-3.5" />}
          {total === 0
            ? t('reponsesRapides.creer', 'Créer une réponse')
            : t('reponsesRapides.gerer', 'Gérer les réponses')}
        </button>
      )}
    </div>
  )
}

interface GererProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  darkMode: boolean
  /** Ouvrir directement sur le formulaire vide (liste encore vide). */
  nouvelleDemandee?: boolean
}

type Brouillon = { id: number | null; title: string; text: string }

/** Fenêtre des gestionnaires : créer, modifier, supprimer les réponses. */
export function GererReponses({ open, onOpenChange, darkMode, nouvelleDemandee = false }: GererProps) {
  const { t } = useTranslation()
  const { data: reponses = [], isLoading } = useReponsesRapides(open)
  const creer = useCreerReponse()
  const modifier = useModifierReponse()
  const supprimer = useSupprimerReponse()
  const [brouillon, setBrouillon] = useState<Brouillon | null>(null)
  const [aSupprimer, setASupprimer] = useState<number | null>(null)
  const [ouvertAvant, setOuvertAvant] = useState(false)

  // À chaque ouverture : formulaire vide si on vient de « Créer une réponse ».
  if (open !== ouvertAvant) {
    setOuvertAvant(open)
    if (open) {
      setBrouillon(nouvelleDemandee ? { id: null, title: '', text: '' } : null)
      setASupprimer(null)
    }
  }

  const enCours = creer.isPending || modifier.isPending
  const valide = !!brouillon && !!brouillon.title.trim() && !!brouillon.text.trim()

  const enregistrer = () => {
    if (!brouillon || !valide || enCours) return
    const corps = { title: brouillon.title.trim(), text: brouillon.text.trim() }
    const fin = {
      onSuccess: () => setBrouillon(null),
      onError: () => toast.error(t('reponsesRapides.erreur', "La réponse n'a pas été enregistrée")),
    }
    if (brouillon.id === null) creer.mutate(corps, fin)
    else modifier.mutate({ id: brouillon.id, ...corps }, fin)
  }

  const effacer = (id: number) => {
    supprimer.mutate(id, {
      onSuccess: () => setASupprimer(null),
      onError: () => toast.error(t('reponsesRapides.erreurSuppression', "La réponse n'a pas été supprimée")),
    })
  }

  const champ = darkMode ? 'bg-slate-800 border-slate-700 text-white placeholder:text-slate-500' : ''
  const discret = darkMode ? 'text-slate-400' : 'text-slate-500'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('sm:max-w-lg', darkMode && 'bg-slate-900 border-slate-800 text-white')}>
        <DialogHeader>
          <DialogTitle>{t('reponsesRapides.titre', 'Réponses enregistrées')}</DialogTitle>
          <DialogDescription className={discret}>
            {t('reponsesRapides.description', 'Tapez /')}
          </DialogDescription>
        </DialogHeader>

        {brouillon ? (
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="reponse-titre">
                {t('reponsesRapides.champTitre', 'Titre')}
              </label>
              <Input
                id="reponse-titre"
                autoFocus
                maxLength={TITRE_MAX}
                value={brouillon.title}
                onChange={(e) => setBrouillon({ ...brouillon, title: e.target.value })}
                placeholder={t('reponsesRapides.champTitreExemple', "Ex. Heures d'ouverture")}
                className={champ}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="reponse-texte">
                {t('reponsesRapides.champTexte', 'Texte')}
              </label>
              <Textarea
                id="reponse-texte"
                rows={5}
                maxLength={TEXTE_MAX}
                value={brouillon.text}
                onChange={(e) => setBrouillon({ ...brouillon, text: e.target.value })}
                placeholder={t('reponsesRapides.champTexteExemple', 'Le texte du message')}
                className={champ}
              />
              <p className={cn('text-right text-xs tabular-nums', discret)}>
                {brouillon.text.length} / {TEXTE_MAX}
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setBrouillon(null)} disabled={enCours}>
                {t('reponsesRapides.annuler', 'Annuler')}
              </Button>
              <Button
                className="bg-indigo-600 text-white hover:bg-indigo-700"
                onClick={enregistrer}
                disabled={!valide || enCours}
              >
                {enCours && <Loader2 className="h-4 w-4 animate-spin" />}
                {t('reponsesRapides.enregistrer', 'Enregistrer')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {isLoading ? (
              <div className={cn('flex justify-center py-6', discret)}>
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : reponses.length === 0 ? (
              <p className={cn('py-4 text-center text-sm', discret)}>
                {t('reponsesRapides.aucune', 'Aucune réponse enregistrée')}
              </p>
            ) : (
              <ul
                className={cn(
                  'max-h-80 overflow-y-auto divide-y rounded-md border',
                  darkMode ? 'divide-slate-800 border-slate-800' : 'divide-slate-100 border-slate-200',
                )}
              >
                {reponses.map((r) => (
                  <li key={r.id} className="flex items-start gap-2 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{r.title}</p>
                      <p className={cn('line-clamp-2 text-xs', discret)}>{r.text}</p>
                    </div>
                    {aSupprimer === r.id ? (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => effacer(r.id)}
                        disabled={supprimer.isPending}
                      >
                        {t('reponsesRapides.confirmerSuppression', 'Oui, supprimer')}
                      </Button>
                    ) : (
                      <>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          title={t('reponsesRapides.modifier', 'Modifier')}
                          onClick={() => setBrouillon({ id: r.id, title: r.title, text: r.text })}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-red-500 hover:text-red-600"
                          title={t('reponsesRapides.supprimer', 'Supprimer')}
                          onClick={() => setASupprimer(r.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <Button
              className="w-full bg-indigo-600 text-white hover:bg-indigo-700"
              onClick={() => setBrouillon({ id: null, title: '', text: '' })}
            >
              <Plus className="h-4 w-4" />
              {t('reponsesRapides.nouvelle', 'Nouvelle réponse')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
